import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, TableHead, TableRow, TableCell, TableBody, Checkbox } from '@mui/material';
import { BackdropCustom, DialogButton, DialogTitle, SecondDialogButton, TableStyled, TableGrid, AddDocumentsTableCell } from './DocumentsStyles';
import { Autocomplete, Dialog, FakeText, TextField } from '@totalsoft/rocket-ui';

const DocumentsAvailableUploadListComponent = ({
  showModal,
  onCloseAvailableUploadListModal,
  availableUploadDocuments,
  onRowSelectionChanged,
  onAddUploadDocuments,
  onFilterValueChanged,
  filter,
  partners,
  onPartnerChange,
  assets,
  onAssetChange
}) => {
  const { t } = useTranslation();

  const closeModal = useCallback(() => {
    onCloseAvailableUploadListModal();
  }, [onCloseAvailableUploadListModal]);

  const addDocuments = useCallback(() => {
    onAddUploadDocuments();
  }, [onAddUploadDocuments]);

  const onLocalRowSelectionChanged = document => event => {
    onRowSelectionChanged(document.attachmentId, event.target.checked);
  };

  const onLocalPartnerChange = (document, partners) => value => {
    const option = partners.find(x => x.partnerId === value);
    onPartnerChange(document.attachmentId, option);
  };

  const onLocalAssetChange = (document, assets) => value => {
    const option = assets.find(x => x.documentDetailId === value);
    onAssetChange(document.attachmentId, option.documentDetailId, option.assetFullName);
  };

  const onTextFilterChanged = useCallback(
    event => {
      onFilterValueChanged(event);
    },
    [onFilterValueChanged]
  );

  const handleLocalAssetChange = document => onLocalAssetChange(document, assets);

  return (
    <>
      <Dialog
        id='available-upload-documents-dialog'
        open={showModal}
        onClose={closeModal}
        fullWidth
        maxWidth={'lg'}
        BackdropComponent={BackdropCustom}
        transitionDuration={0}
        showX={false}
        disableBackdropClick
        disableEscapeKeyDown
        actionsProps={{ sx: theme => ({ px: theme.spacing(3), pb: theme.spacing(2) }) }}
        title={
          <Grid container>
            <Grid item xs={12} mb={3}>
              <DialogTitle>{t('OfferEdit.Documents.Title')}</DialogTitle>
            </Grid>
            {availableUploadDocuments?.isLoaded && (
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <TextField placeholder={t('Search')} fullWidth name='fullTextFilter' value={filter} onChange={onTextFilterChanged} />
              </Grid>
          )}
            <Grid item xs={12}>
              <TableStyled >
                <TableHead>
                  <TableRow>
                    <AddDocumentsTableCell sx={{ width: '7%' }}></AddDocumentsTableCell>
                    <AddDocumentsTableCell sx={{ width: '56%' }}>{t('OfferEdit.Documents.DocumentName')}</AddDocumentsTableCell>
                    <AddDocumentsTableCell sx={{ width: '17%' }}>{t('OfferEdit.Documents.Context')}</AddDocumentsTableCell>
                    <AddDocumentsTableCell sx={{ width: '20%' }}>{t('OfferEdit.Documents.EntityTitle')}</AddDocumentsTableCell>
                  </TableRow>
                </TableHead>
              </TableStyled>
            </Grid>
          </Grid>
        }
        content={
          !availableUploadDocuments?.isLoaded ? (
            <FakeText lines={7} />
          ) : (
            <TableGrid xs={12} sm={12} md={12}>
              <TableStyled>
                <TableBody>
                  {availableUploadDocuments.map(document => {
                    return (
                      <TableRow key={document.attachmentId}>
                        <TableCell sx={{ width: '7%' }}>
                          <Checkbox checked={document.selected || false} onChange={onLocalRowSelectionChanged(document)} value='1' />
                        </TableCell>
                        <TableCell  sx={{ width: '56%' }}>{document.attachmentTypeName}</TableCell>
                        <TableCell sx={{ width: '17%' }}>{document.contextName}</TableCell>
                        <TableCell sx={{ width: '20%' }} >
                          {document.contextId === -1 && (
                            <Autocomplete
                              fullWidth
                              value={document.entityId}
                              isSearchable={false}
                              simpleValue={true}
                              onChange={onLocalPartnerChange(document, partners)}
                              valueKey='partnerId'
                              labelKey='partnerName'
                              id='partner'
                              options={partners}
                              isClearable={false}
                            />
                          )}
                          {document.contextId === -5 && (
                            <Autocomplete
                              fullWidth
                              value={document.entityId}
                              isSearchable={false}
                              simpleValue={true}
                              onChange={handleLocalAssetChange(document)}
                              valueKey='internalId'
                              labelKey='assetFullName'
                              id='asset'
                              options={assets}
                              isClearable={false}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </TableStyled>
            </TableGrid>
          )
        }
        actions={
          <>
            <DialogButton color='primary' variant='contained' onClick={addDocuments}>
              {t('Add')}
            </DialogButton>
            <SecondDialogButton color='primary' variant='outlined' onClick={closeModal}>
              {t('Close')}
            </SecondDialogButton>
          </>
        }
      />
    </>
  );
};

DocumentsAvailableUploadListComponent.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onCloseAvailableUploadListModal: PropTypes.func.isRequired,
  availableUploadDocuments: PropTypes.array,
  onRowSelectionChanged: PropTypes.func.isRequired,
  onAddUploadDocuments: PropTypes.func.isRequired,
  onFilterValueChanged: PropTypes.func.isRequired,
  filter: PropTypes.string,
  partners: PropTypes.array,
  assets: PropTypes.array,
  onPartnerChange: PropTypes.func.isRequired,
  onAssetChange: PropTypes.func.isRequired
};

export default DocumentsAvailableUploadListComponent;
